/**
 * List Autocomplete Component
 * Autocomplete input for selecting lists
 */

import React, { useState, useEffect, useRef } from "react";
import { getLists } from "../../services/lists.service";
import type { List } from "../../types/list.types";
import type { UUID } from "../../types/common.types";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

interface ListAutocompleteProps {
  value: UUID;
  onChange: (listId: UUID) => void;
  placeholder?: string;
  error?: string;
  organizationId?: UUID;
  disabled?: boolean;
  /**
   * Restrict the picker to static segments only. Used by FlightForm so
   * operators can't pick a dynamic list and discover at send-time that
   * resolveAudience refuses dynamic membership. Static-only is the
   * tactical pairing for the 2026-05-12 audience pagination fix —
   * remove this flag when dynamic-list flights are properly supported.
   */
  staticOnly?: boolean;
  /**
   * Called once when an initial `value` is provided but doesn't resolve
   * to any list in the loaded set — i.e. the saved list_id is stale
   * (segment was deleted). The parent should treat this as a validation
   * blocker on save/schedule. Component continues to render so the user
   * sees the stale state and can pick a replacement; it does NOT
   * silently clear `value` for the parent.
   */
  onStaleValue?: (staleId: UUID) => void;
}

const ListAutocomplete: React.FC<ListAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Search for a list...",
  error,
  organizationId,
  disabled = false,
  staticOnly = false,
  onStaleValue,
}) => {
  const [lists, setLists] = useState<List[]>([]);
  const [filteredLists, setFilteredLists] = useState<List[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedList, setSelectedList] = useState<List | null>(null);
  const [staleId, setStaleId] = useState<UUID | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Load lists
  useEffect(() => {
    const loadLists = async () => {
      setIsLoading(true);
      try {
        const raw = await getLists(organizationId);
        // Static-only filter is applied here (before the dropdown ever
        // sees the data) so search results are also limited. The
        // stale-id detection below still operates on the visible set —
        // a saved dynamic list_id will surface as stale, which is the
        // intended UX: the operator gets a "previously selected segment
        // no longer eligible" prompt and has to pick a static one.
        const data = staticOnly ? raw.filter((l) => !l.is_dynamic) : raw;
        setLists(data);
        setFilteredLists(data);

        // Find selected list if value is set. If the value doesn't
        // resolve, mark it as stale so the parent can block save and
        // the user sees the explicit "previously selected segment
        // no longer exists" message below.
        if (value) {
          const selected = data.find((list) => list.id === value);
          if (selected) {
            setSelectedList(selected);
            setSearchTerm(selected.name);
            setStaleId(null);
          } else {
            setSelectedList(null);
            setSearchTerm("");
            setStaleId(value);
            onStaleValue?.(value);
          }
        } else {
          setStaleId(null);
        }
      } catch (error) {
        console.error("Error loading lists:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLists();
    // onStaleValue intentionally omitted from deps — we only want to
    // fire it once per (value, organizationId) load cycle, not on every
    // identity change of the callback prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, value, staticOnly]);

  // Filter lists based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredLists(lists);
    } else {
      const filtered = lists.filter((list) =>
        list.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredLists(filtered);
    }
  }, [searchTerm, lists]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    if (!e.target.value) {
      setSelectedList(null);
      onChange("");
    }
  };

  const handleSelectList = (list: List) => {
    setSelectedList(list);
    setSearchTerm(list.name);
    setStaleId(null);
    onChange(list.id);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSearchTerm("");
    setSelectedList(null);
    setStaleId(null);
    onChange("");
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={preventEnterSubmit}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className={`block w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors ${
            error
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
        />
        {searchTerm && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && filteredLists.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredLists.map((list) => (
            <button
              key={list.id}
              type="button"
              onClick={() => handleSelectList(list)}
              className={`w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                selectedList?.id === list.id ? "bg-blue-50" : ""
              }`}
            >
              <div className="font-medium text-gray-900">{list.name}</div>
              {list.description && (
                <div className="text-sm text-gray-600">{list.description}</div>
              )}
              <div className="text-xs text-gray-500 mt-1">
                {list.contact_count || 0} contacts •{" "}
                {list.is_dynamic ? "Dynamic" : "Static"}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {isOpen && !disabled && searchTerm && filteredLists.length === 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500">
          No segments found matching "{searchTerm}"
        </div>
      )}

      {/* Error message */}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* Selected list info */}
      {selectedList && !isOpen && (
        <p className="mt-1 text-xs text-gray-600">
          Selected: {selectedList.name} ({selectedList.contact_count || 0}{" "}
          contacts)
        </p>
      )}

      {/* Stale value warning — surfaces when the saved value doesn't
          resolve to any current list. Parent should block save until a
          replacement is picked. */}
      {staleId && !selectedList && !isLoading && (
        <p className="mt-1 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Previously selected segment is no longer eligible:{" "}
          <code className="font-mono text-xs">{staleId}</code>. It may have been
          deleted, or it's a dynamic segment that isn't supported in this
          context. Select a current segment.
        </p>
      )}
    </div>
  );
};

export default ListAutocomplete;
